import React from 'react';
import { BadgeCheck, Clock, Loader2, ShieldCheck } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button, Surface, Text } from '@/components/flup';
import { cn } from '@/lib/utils';

/**
 * The wait between paying and being on the plan.
 *
 * Razorpay hands back an authorised mandate, but the venue only moves to
 * SUBSCRIPTION on the `subscription.charged` webhook — seconds later, sometimes
 * longer. Without a screen for that gap the vendor pays and lands back on a
 * card that still says "pay per booking", which reads as a failed payment, so
 * this holds the window: no close button and no outside-click while the plan is
 * being confirmed, then it says plainly how it ended.
 *
 *   verifying  polling; the money is taken, the plan has not flipped yet
 *   active     the webhook landed, the venue is on the subscription
 *   slow       polling gave up; the payment is safe, activation is still coming
 *   checking   checkout was closed without an authorisation; asking the server
 *              whether the mandate went through anyway before handing back
 */

export type ActivationStatus = 'verifying' | 'active' | 'slow' | 'checking';

interface SubscriptionActivationModalProps {
  status: ActivationStatus | null;
  venueName?: string;
  /** Poll progress, so the bar moves against something real. */
  attempt?: number;
  totalAttempts?: number;
  onClose: () => void;
  onRetry: () => void;
}

const SubscriptionActivationModal: React.FC<SubscriptionActivationModalProps> = ({
  status,
  venueName,
  attempt = 0,
  totalAttempts = 1,
  onClose,
  onRetry,
}) => {
  const verifying = status === 'verifying';
  const checking = status === 'checking';
  /** Both states are waiting on the server and must not be dismissed. */
  const waiting = verifying || checking;

  // Never a full bar while we are still waiting — the last step is the webhook,
  // not the timer.
  const progress = verifying
    ? Math.min(92, Math.round((attempt / Math.max(totalAttempts, 1)) * 100))
    : 100;

  return (
    <Dialog open={Boolean(status)} onOpenChange={(next) => { if (!next && !waiting) onClose(); }}>
      <DialogContent
        className={cn('max-w-md', waiting && '[&>button]:hidden')}
        onInteractOutside={(e) => { if (waiting) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (waiting) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>
            {status === 'active'
              ? 'You are on zero commission'
              : status === 'slow'
                ? 'Payment received'
                : checking
                  ? 'Checking payment status'
                  : 'Confirming your subscription'}
          </DialogTitle>
          <DialogDescription>
            {status === 'active'
              ? `${venueName ? `${venueName} is` : 'This venue is'} on the fixed subscription. No commission is taken on bookings from now on.`
              : status === 'slow'
                ? 'Your payment went through. Activating the plan is taking longer than usual, and nothing more is needed from you.'
                : checking
                  ? 'The payment window was closed. We are checking with Razorpay whether your payment went through.'
                  : 'Your payment is done. We are waiting for your bank to confirm it and for the plan to switch over.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid place-items-center gap-3 py-4">
          <div className="relative w-14 h-14">
          {/* A ring pulsing out of the badge while we wait — the spinner alone
              reads as stuck once it has been on screen for a few seconds. */}
          {waiting ? (
            <span className="absolute inset-0 rounded-full bg-flup-brand/30 animate-ping" aria-hidden />
          ) : null}
          <div
            className={cn(
              'relative w-14 h-14 rounded-full grid place-items-center',
              status === 'active'
                ? 'bg-flup-green-soft'
                : status === 'slow'
                  ? 'bg-flup-warning-soft'
                  : 'bg-flup-brand-soft',
            )}
          >
            {status === 'active' ? (
              <BadgeCheck className="w-7 h-7 text-flup-green" aria-hidden />
            ) : status === 'slow' ? (
              <Clock className="w-7 h-7 text-flup-warning" aria-hidden />
            ) : (
              <Loader2 className="w-7 h-7 text-flup-brand animate-spin" aria-hidden />
            )}
          </div>
          </div>

          {checking ? (
            <Text variant="caption" tone="tertiary" as="p" className="text-center" role="status">
              This takes a few seconds. Please do not close or refresh this page.
            </Text>
          ) : null}

          {verifying ? (
            <>
              <Text variant="bodySemi" as="p" className="text-center">
                Please wait while we validate your subscription
              </Text>
              <div
                className="w-full h-1.5 rounded-full bg-flup-surface-alt overflow-hidden"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress}
                aria-label="Validating your subscription"
              >
                <div
                  className="h-full bg-flup-brand transition-[width] duration-500"
                  style={{ width: `${Math.max(progress, 8)}%` }}
                />
              </div>
              <Text variant="caption" tone="tertiary" as="p" className="text-center">
                This usually takes a few seconds. Please do not close or refresh this page.
              </Text>
            </>
          ) : null}
        </div>

        {checking ? null : verifying ? (
          <Surface padding="p-3" className="bg-flup-surface-alt">
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-flup-ink-3 shrink-0 mt-0.5" aria-hidden />
              <Text variant="caption" tone="secondary" as="p">
                Your money is already safe with Razorpay. If the plan does not switch over right
                away, it will be updated automatically — you will not be charged twice.
              </Text>
            </div>
          </Surface>
        ) : (
          <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
            {status === 'slow' ? (
              <Button fullWidth variant="secondary" onClick={onRetry}>
                Check again
              </Button>
            ) : null}
            <Button fullWidth variant={status === 'slow' ? 'ghost' : 'primary'} onClick={onClose}>
              {status === 'slow' ? 'I will check later' : 'Done'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionActivationModal;
